import React, { useEffect, useState, useCallback } from "react";
import { Edit2, Trash2, Plus, Tag, X, Save, ShieldCheck, ShieldAlert, ShieldX, Download } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { imgSrc } from "../../lib/imgSrc";
import ImageExplorer from "./ImageExplorer";

const CONSENT_STATUS_OPTS = [
    { value: "not_required", label: "Not required", icon: ShieldCheck, tone: "neutral" },
    { value: "pending",      label: "Pending",      icon: ShieldAlert, tone: "amber" },
    { value: "granted",      label: "Granted",      icon: ShieldCheck, tone: "green" },
    { value: "revoked",      label: "Revoked",      icon: ShieldX,     tone: "red" },
];
const CONSENT_SCOPES = [
    { value: "public_web",  label: "Public website" },
    { value: "print",       label: "Print materials" },
    { value: "fundraising", label: "Fundraising / appeals" },
];

const ConsentPill = ({ status }) => {
    const opt = CONSENT_STATUS_OPTS.find((o) => o.value === status) || CONSENT_STATUS_OPTS[0];
    const tone = {
        neutral: "bg-[#F4F1EA] text-[#4A5568]",
        amber: "bg-amber-100 text-amber-900",
        green: "bg-green-100 text-green-900",
        red: "bg-red-100 text-red-900",
    }[opt.tone];
    const Icon = opt.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold ${tone} rounded-full px-2 py-0.5`}>
            <Icon className="w-3 h-3" strokeWidth={2} /> {opt.label}
        </span>
    );
};

const blankConsent = () => ({
    subject_name: "", guardian_name: "", subject_email: "", subject_phone: "",
    scope: [], expires_at: "", release_url: "", notes: "",
});

const ImageLibraryPanel = () => {
    const [tab, setTab] = useState("library"); // 'library' | 'categories'
    const [categories, setCategories] = useState([]);
    const [folders, setFolders] = useState([]);
    const [editing, setEditing] = useState(null); // image being edited

    const loadCategories = useCallback(async () => {
        try { const { data } = await api.get("/admin/image-categories"); setCategories(data || []); }
        catch { setCategories([]); }
    }, []);

    const loadFolders = useCallback(async () => {
        try { const { data } = await api.get("/admin/image-folders"); setFolders(data || []); }
        catch { setFolders([]); }
    }, []);

    useEffect(() => { loadCategories(); }, [loadCategories]);
    useEffect(() => { loadFolders(); }, [loadFolders]);

    return (
        <div className="space-y-6" data-testid="image-library-panel">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E]">Image library</h2>
                    <p className="text-sm text-[#4A5568] mt-1">
                        Categorized image library with folders. Drag and drop to upload. Subject consent required for images of identifiable people.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setTab("library")} data-testid="image-library-tab-library" className={`px-4 py-2 rounded-full text-sm font-medium border ${tab === "library" ? "bg-[#1C252E] text-white border-[#1C252E]" : "bg-white border-black/10"}`}>Library</button>
                    <button onClick={() => setTab("categories")} data-testid="image-library-tab-categories" className={`px-4 py-2 rounded-full text-sm font-medium border ${tab === "categories" ? "bg-[#1C252E] text-white border-[#1C252E]" : "bg-white border-black/10"}`}>Categories</button>
                </div>
            </div>

            {tab === "library" ? (
                <ImageExplorer
                    categories={categories}
                    folders={folders}
                    refreshFolders={loadFolders}
                    onEditImage={(img) => setEditing(img)}
                />
            ) : (
                <CategoriesEditor categories={categories} reload={loadCategories} />
            )}

            {editing && (
                <EditImageModal
                    image={editing}
                    categories={categories}
                    onClose={() => setEditing(null)}
                    onSaved={() => setEditing(null)}
                />
            )}
        </div>
    );
};

// ============================================================
// Edit modal (full image metadata + consent)
// ============================================================
const EditImageModal = ({ image, categories, onClose, onSaved }) => {
    const [form, setForm] = useState({
        title: image.title || "",
        caption: image.caption || "",
        category_slug: image.category_slug || "misc",
        folder_path: image.folder_path || "",
        tags: (image.tags || []).join(", "),
        is_public: image.is_public !== false,
        consent_required: !!image.consent_required,
        consent_status: image.consent_status || "not_required",
        consent: image.consent ? { ...blankConsent(), ...image.consent, scope: image.consent.scope || [] } : blankConsent(),
    });
    const [saving, setSaving] = useState(false);
    const [folders, setFolders] = useState([]);

    useEffect(() => {
        api.get("/admin/image-folders", { params: { category: form.category_slug } })
            .then((r) => setFolders(r.data || []))
            .catch(() => setFolders([]));
    }, [form.category_slug]);

    const updateConsent = (patch) => setForm((p) => ({ ...p, consent: { ...p.consent, ...patch } }));
    const toggleScope = (s) => updateConsent({ scope: form.consent.scope.includes(s) ? form.consent.scope.filter((x) => x !== s) : [...form.consent.scope, s] });

    const onSave = async () => {
        setSaving(true);
        try {
            const body = {
                title: form.title.trim(),
                caption: form.caption,
                category_slug: form.category_slug,
                folder_path: form.folder_path || "",
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                is_public: form.is_public,
                consent_required: form.consent_required,
                consent_status: form.consent_status,
            };
            if (form.consent_required) {
                body.consent = { ...form.consent };
                // Strip empty optional fields so backend doesn't store empty strings
                Object.keys(body.consent).forEach((k) => {
                    if (body.consent[k] === "" || (Array.isArray(body.consent[k]) && body.consent[k].length === 0 && k !== "scope")) {
                        delete body.consent[k];
                    }
                });
                if (!body.consent.subject_name) { toast.error("Subject name is required when consent is required."); setSaving(false); return; }
            }
            const { data } = await api.patch(`/admin/image-library/${image.id}`, body);
            toast.success("Saved");
            onSaved(data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="image-library-edit-modal">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
            <div className="relative w-full max-w-2xl bg-[#FDFBF7] h-full overflow-y-auto shadow-2xl">
                <header className="sticky top-0 bg-[#FDFBF7]/95 backdrop-blur border-b border-black/10 px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="font-heading text-xl text-[#1C252E]">Edit image</h2>
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={async () => {
                                try {
                                    const res = await api.get(`/admin/image-library/${image.id}/download`, { responseType: "blob" });
                                    const url = window.URL.createObjectURL(res.data);
                                    const cd = res.headers["content-disposition"] || "";
                                    const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
                                    const fname = m ? m[1] : `${(image.title || "image").replace(/[^A-Za-z0-9._-]+/g, "_")}.jpg`;
                                    const a = document.createElement("a");
                                    a.href = url; a.download = fname;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    setTimeout(() => window.URL.revokeObjectURL(url), 5000);
                                    toast.success("Download started");
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Download failed");
                                }
                            }}
                            data-testid="image-edit-download"
                            aria-label="Download original"
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full border border-black/10 hover:bg-[#F4F1EA]"
                        >
                            <Download className="w-3.5 h-3.5" strokeWidth={2} /> Download
                        </button>
                        <button onClick={onClose} aria-label="Close" data-testid="image-library-edit-close" className="p-2 rounded-full hover:bg-black/5">
                            <X className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                    </div>
                </header>

                <div className="p-6 space-y-5">
                    <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
                        <img src={imgSrc(image.url, 900, 82)} alt={image.title} loading="lazy" decoding="async" className="w-full max-h-72 object-contain bg-[#F4F1EA]" />
                        <p className="px-4 py-2 text-[11px] text-[#4A5568] break-all">{image.url}</p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Title</span>
                            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="image-edit-title" className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Category</span>
                            <select value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value, folder_path: "" })} data-testid="image-edit-category" className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm">
                                {categories.filter((c) => c.is_active !== false).map((c) => <option key={c.id} value={c.slug}>{c.label}</option>)}
                            </select>
                        </label>
                    </div>

                    <label className="block">
                        <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Folder</span>
                        <select value={form.folder_path} onChange={(e) => setForm({ ...form, folder_path: e.target.value })} data-testid="image-edit-folder" className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm">
                            <option value="">— No folder —</option>
                            {folders.map((f) => <option key={f.id} value={f.path}>{f.path}</option>)}
                        </select>
                        {folders.length === 0 && (
                            <p className="text-[11px] text-[#4A5568] mt-1 italic">No folders in this category yet. Create one in the Folders tab.</p>
                        )}
                    </label>

                    <label className="block">
                        <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Caption</span>
                        <textarea rows={2} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="image-edit-caption" className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                    </label>

                    <label className="block">
                        <span className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Tags <span className="text-[#4A5568] font-normal normal-case text-[10px]">(comma-separated)</span></span>
                        <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. spring, stem, classroom" data-testid="image-edit-tags" className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                    </label>

                    <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-black/5">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} data-testid="image-edit-is-public" />
                            <span className="text-sm">Visible in picker (uncheck to hide)</span>
                        </label>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.consent_required} onChange={(e) => setForm({ ...form, consent_required: e.target.checked, consent_status: e.target.checked ? (form.consent_status === "not_required" ? "pending" : form.consent_status) : "not_required" })} data-testid="image-edit-consent-required" />
                            <span className="text-sm">Image shows an identifiable person (consent required)</span>
                        </label>
                    </div>

                    {form.consent_required && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3" data-testid="image-edit-consent-block">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <h3 className="font-heading text-sm text-amber-900 flex items-center gap-1">
                                    <ShieldAlert className="w-4 h-4" strokeWidth={2} /> Subject consent record
                                </h3>
                                <select value={form.consent_status} onChange={(e) => setForm({ ...form, consent_status: e.target.value })} data-testid="image-edit-consent-status" className="text-xs rounded-full bg-white border border-amber-300 px-3 py-1">
                                    {CONSENT_STATUS_OPTS.filter((o) => o.value !== "not_required").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input value={form.consent.subject_name} onChange={(e) => updateConsent({ subject_name: e.target.value })} placeholder="Subject name *" data-testid="image-edit-consent-subject" className="rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                                <input value={form.consent.guardian_name} onChange={(e) => updateConsent({ guardian_name: e.target.value })} placeholder="Guardian name (if minor)" data-testid="image-edit-consent-guardian" className="rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                                <input value={form.consent.subject_email} onChange={(e) => updateConsent({ subject_email: e.target.value })} placeholder="Subject / guardian email" data-testid="image-edit-consent-email" className="rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                                <input value={form.consent.subject_phone} onChange={(e) => updateConsent({ subject_phone: e.target.value })} placeholder="Phone" data-testid="image-edit-consent-phone" className="rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                                <input type="date" value={form.consent.expires_at} onChange={(e) => updateConsent({ expires_at: e.target.value })} placeholder="Expires" data-testid="image-edit-consent-expires" className="rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                                <input value={form.consent.release_url} onChange={(e) => updateConsent({ release_url: e.target.value })} placeholder="Release form URL / PDF" data-testid="image-edit-consent-release" className="rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                            </div>
                            <div>
                                <p className="text-xs text-amber-900 mb-1">Permission scope</p>
                                <div className="flex flex-wrap gap-2">
                                    {CONSENT_SCOPES.map((s) => (
                                        <button
                                            key={s.value}
                                            type="button"
                                            onClick={() => toggleScope(s.value)}
                                            data-testid={`image-edit-consent-scope-${s.value}`}
                                            className={`text-xs px-3 py-1 rounded-full border ${form.consent.scope.includes(s.value) ? "bg-amber-900 text-white border-amber-900" : "bg-white text-amber-900 border-amber-300"}`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <textarea rows={2} value={form.consent.notes} onChange={(e) => updateConsent({ notes: e.target.value })} placeholder="Notes (where the release is filed, how consent was obtained, etc.)" data-testid="image-edit-consent-notes" className="w-full rounded-xl border border-amber-300 px-3 py-2 bg-white text-sm" />
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-3 border-t border-black/5">
                        <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium hover:bg-black/5">Cancel</button>
                        <button onClick={onSave} disabled={saving} data-testid="image-edit-save-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium disabled:opacity-60">
                            <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Categories editor
// ============================================================
const CategoriesEditor = ({ categories, reload }) => {
    const [newSlug, setNewSlug] = useState("");
    const [newLabel, setNewLabel] = useState("");
    const [creating, setCreating] = useState(false);

    const create = async () => {
        if (!newSlug.trim() || !newLabel.trim()) { toast.error("Slug and label are required"); return; }
        setCreating(true);
        try {
            await api.post("/admin/image-categories", { slug: newSlug.trim().toLowerCase(), label: newLabel.trim() });
            setNewSlug(""); setNewLabel("");
            await reload();
            toast.success("Category added");
        } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
        finally { setCreating(false); }
    };

    const rename = async (c) => {
        const next = window.prompt("New label", c.label);
        if (!next || next.trim() === c.label) return;
        try { await api.patch(`/admin/image-categories/${c.id}`, { label: next.trim() }); await reload(); toast.success("Renamed"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
    };

    const toggle = async (c) => {
        try { await api.patch(`/admin/image-categories/${c.id}`, { is_active: !c.is_active }); await reload(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete category "${c.label}"? Images must be moved first.`)) return;
        try { await api.delete(`/admin/image-categories/${c.id}`); await reload(); toast.success("Removed"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="space-y-4" data-testid="image-categories-editor">
            <div className="bg-white border border-black/10 rounded-2xl p-4 flex flex-wrap gap-3 items-center">
                <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="slug (lowercase, dashes)" data-testid="image-cat-new-slug" className="rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Label" data-testid="image-cat-new-label" className="rounded-xl border border-black/10 px-3 py-2 bg-white text-sm" />
                <button onClick={create} disabled={creating} data-testid="image-cat-new-btn" className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-60">
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add category
                </button>
            </div>

            <ul className="space-y-2">
                {categories.map((c) => (
                    <li key={c.id} className="bg-white border border-black/10 rounded-2xl px-4 py-3 flex items-center justify-between gap-3" data-testid={`image-cat-row-${c.slug}`}>
                        <div>
                            <p className="text-sm font-bold text-[#1C252E]">{c.label} {c.is_seed && <span className="text-[10px] uppercase tracking-wider font-normal text-[#4A5568] ml-1">seed</span>} {!c.is_active && <span className="text-[10px] uppercase tracking-wider font-bold text-red-900 ml-1">disabled</span>}</p>
                            <p className="text-[11px] text-[#4A5568] font-mono"><Tag className="w-3 h-3 inline-block mr-0.5" strokeWidth={1.5} />{c.slug}</p>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => rename(c)} aria-label="Rename" data-testid={`image-cat-rename-${c.slug}`} className="p-2 rounded-full hover:bg-black/5"><Edit2 className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} /></button>
                            <button onClick={() => toggle(c)} data-testid={`image-cat-toggle-${c.slug}`} className="text-xs font-medium rounded-full border border-black/10 px-3 py-1">{c.is_active ? "Disable" : "Enable"}</button>
                            <button onClick={() => remove(c)} aria-label="Delete" data-testid={`image-cat-remove-${c.slug}`} className="p-2 rounded-full hover:bg-red-50"><Trash2 className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} /></button>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};


export default ImageLibraryPanel;
